import { useQuery } from '@tanstack/react-query';
import { CoinsQueryKey } from '../../queryKeys';
import { UseCoinByIdProps, UseCoinByIdQueryKey } from './props';
import { Coin, GenericError } from '@starsoft/common/models';
import { AxiosError, AxiosInstance } from 'axios';
import { fetchCoinById } from './service';
import { useAxiosInstance } from '@starsoft/common/hooks';

/**
 * Custom hook to fetch a coin by its ID.
 *
 * @param {UseCoinByIdProps} options - Configuration options for the query.
 * @param {string} options.id - The ID of the coin to fetch.
 *
 * @returns {Object} - Returns an object containing the query state and functions.
 * @returns {Coin} return.coin - The fetched coin data.
 * @returns {boolean} return.isPending - Indicates if the query is currently loading.
 * @returns {AxiosError<GenericError>} return.error - Error object if the query fails.
 * @returns {Function} return.refetch - Function to manually refetch the data.
 */
export function useCoinById({ id }: UseCoinByIdProps) {
  // Define the query key for caching and refetching purposes
  const queryKey: UseCoinByIdQueryKey = [CoinsQueryKey.ById, id];

  // Get an Axios instance for making HTTP requests
  const axiosInstance: AxiosInstance = useAxiosInstance();

  // Use the useQuery hook to fetch coin data by ID
  const { data, isPending, error, refetch, fetchStatus } = useQuery<
    Coin,
    AxiosError<GenericError>,
    Coin,
    UseCoinByIdQueryKey
  >({
    queryKey,
    queryFn: queryContext => fetchCoinById(queryContext, axiosInstance),
    staleTime: 5 * 1000, // Data is considered fresh for 5 seconds
    enabled: !!id, // Enable the query only if the ID is provided
  });

  // Return the query state and functions
  return {
    coin: data, // The fetched coin data
    isPending: isPending && fetchStatus !== 'idle', // Indicates if the query is currently loading
    error, // Error object if the query fails
    refetch, // Function to manually refetch the data
  };
}
